import { SyntheticEvent, useEffect, useMemo, useRef } from "react"
import { useToggle } from "react-use"
import { useParams } from "react-router-dom"

import { Column, Columns, Text } from "ui-kit/components-branded/index"
import { MySessionsLink, ProfileLink } from "app/AppRouter/links"
import { ContactsModal, Icon } from "components"
import { usePaymentContext } from "modules/Payment/PaymentContext"
import { BookingStatusMap, PaymentSystemType } from "utils/interfaces"
import { CURRENCY } from "utils/constants"
import { getStartDate } from "modules/Payment/common/utils"
import { fetchBookingDetails } from "modules/Payment/common/api"
import { Spinner } from "components/Spinner"
import { BrowserWarningBanner, BrowserWarningType } from "components/BrowserWarningBanner"
import { PAYMENT_DONE_PARAMETER } from "modules/BookingCommon/common/constants"
import { SessionDetails } from "modules/Booking/AfterPaymentPage/components"
import { getSingleOrFamilyText } from "modules/BookingCommon/common/utils"
import { GiftCertificateBanner, OnlineOrOfflineLabel } from "modules/BookingCommon/components"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import {
    formatTitlesShort,
    getFaceUrl,
    renderCityWithDetails,
} from "utils/helpers/psychologistUtil"
import { formatPrice } from "utils/utils"

export function SuccessPage({ needLoadingDetails }: { needLoadingDetails?: boolean }) {
    const [
        {
            isOnline,
            bookingDetails: {
                psy,
                service_type,
                rfc3339_start_datetime,
                psy_full_name,
                psy_phone,
                payment_system,
                price,
                price_usd,
                client_email,
                client_phone,
                is_online,
            },
            bookingStatus,
        },
    ] = usePaymentContext()
    const [isLoading, toggleIsLoading] = useToggle(true)
    const [error, toggleError] = useToggle(false)
    const [isModalShown, toggleModalIsShown] = useToggle(false)
    const { id } = useParams<{ id: string }>()
    const countRef = useRef(0)

    useEffect(() => {
        function getBookingStatus() {
            const waitStatusUpdate =
                new URLSearchParams(window.location.search).get(PAYMENT_DONE_PARAMETER) === "1"

            if (bookingStatus === BookingStatusMap.PAID || needLoadingDetails) {
                toggleIsLoading()
            }

            if (
                bookingStatus === BookingStatusMap.PAY_STARTED &&
                waitStatusUpdate &&
                !needLoadingDetails &&
                id
            ) {
                fetchBookingDetails(id)
                    .then(() => {
                        if (countRef.current < 20) {
                            setTimeout(() => {
                                getBookingStatus()
                                countRef.current = countRef.current + 1
                            }, 1000)
                        }
                    })
                    .catch(() => {
                        toggleError(true)
                    })
                    .finally(() => {
                        toggleIsLoading()
                    })
            }
        }

        id && getBookingStatus()
    }, [])

    function handleClickLink(event: SyntheticEvent) {
        event.stopPropagation()
        toggleModalIsShown(true)
    }

    const actualPrice = useMemo(() => {
        if (payment_system === PaymentSystemType.STRIPE) {
            return price_usd
        }
        return price
    }, [payment_system])

    const currency = useMemo(
        () => (payment_system === PaymentSystemType.STRIPE ? CURRENCY.USD : CURRENCY.RUB),
        [payment_system]
    )

    const text = getSingleOrFamilyText(!service_type.family)

    const faceUrl = getFaceUrl(psy.face_url, psy.hash_id)

    if (isLoading) {
        return (
            <div className="redesign-container">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="redesign-container">
            <div className="after-payment">
                <div className="after-payment__left">
                    {error ? (
                        <div>
                            Ошибка. Перезагрузите страницу, а&nbsp;если не&nbsp;помогло,
                            то&nbsp;обратитесь в службу поддержки
                        </div>
                    ) : (
                        <>
                            <SessionDetails
                                sessionType={SESSION_TYPE.DEFAULT}
                                phone={client_phone}
                                email={client_email}
                            />
                            {psy.experiment_group && is_online && (
                                <BrowserWarningBanner
                                    type={BrowserWarningType.Session}
                                    className="after-payment__browser-waring"
                                />
                            )}
                            <div className="after-payment__session-info">
                                <Columns className="after-payment__session-info-top mb-0">
                                    <Column bulma="is-half-tablet">
                                        <div className="session-info-top__with-divider is-flex is-flex-direction-column">
                                            <Text isAccent className="mgb-4">
                                                Информация о&nbsp;сессии
                                            </Text>
                                            <Text className="is-flex no-wrap-on-desktop mgb-4">
                                                <OnlineOrOfflineLabel online={isOnline} />
                                                <span className="after-payment__session-info-bullet">
                                                    •
                                                </span>
                                                {text}
                                            </Text>
                                            {rfc3339_start_datetime && (
                                                <Text className="mgb-4">
                                                    {getStartDate(rfc3339_start_datetime)}
                                                </Text>
                                            )}
                                            <Text>
                                                Оплачено
                                                {actualPrice > 0
                                                    ? `: ${formatPrice(actualPrice, currency)}`
                                                    : ""}
                                            </Text>
                                            {!isOnline && (
                                                <a onClick={handleClickLink} className="mgt-4">
                                                    <Text>Как добраться</Text>
                                                </a>
                                            )}
                                        </div>
                                    </Column>
                                    <Column bulma="is-half-tablet">
                                        <div className="psy-info__with-divider">
                                            <ProfileLink
                                                profileUrl={psy.profile_url}
                                                className="after-payment__session-info__header"
                                                target="_blank"
                                            >
                                                <div className="after-payment__session-info__header__avatar">
                                                    <img
                                                        src={faceUrl}
                                                        alt={psy_full_name}
                                                        srcSet={`${faceUrl} 2x`}
                                                    />
                                                </div>
                                                <div className="after-payment__session-info__header__main-info">
                                                    <div className="mr-8">
                                                        <div className="after-payment__session-info__header__main-info__name">
                                                            {psy_full_name}
                                                        </div>
                                                        <div className="after-payment__session-info__header__main-info__title">
                                                            {formatTitlesShort(psy.titles)}
                                                        </div>
                                                    </div>
                                                    <Icon type="arrow-right-gray" size={24} />
                                                </div>
                                            </ProfileLink>
                                        </div>
                                    </Column>
                                </Columns>
                                <div className="after-payment__session-info__line" />
                                <div className="after-payment__session-info__bottom">
                                    <MySessionsLink target="_blank" className="button-primary">
                                        <Text isAccent>К&nbsp;моим сессиям</Text>
                                    </MySessionsLink>
                                </div>
                                <ContactsModal
                                    isOffline={!isOnline}
                                    isModalShown={isModalShown}
                                    phone={psy_phone}
                                    location={
                                        !isOnline
                                            ? renderCityWithDetails(
                                                  psy.new_city.name,
                                                  psy.addresses,
                                                  psy.city_details,
                                                  <br />
                                              )
                                            : psy.video_call_handle
                                    }
                                    hideModal={() => toggleModalIsShown(false)}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className="after-payment__banners">
                    <GiftCertificateBanner />
                </div>
            </div>
        </div>
    )
}
